import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatKM, formatMoney, formatDate, monthLabel, deadlineLabel } from "@/lib/format";
import { Badge } from "@/components/CrudPage";
import { Card } from "@/components/ui/card";
import {
  Briefcase, Wallet, CalendarClock, FileWarning, ArrowRight, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

function Kpi({ icon: Icon, label, value, sub, tone, to }) {
  const tones = {
    primary: "bg-primary text-primary-foreground",
    warning: "bg-amber-500 text-white",
    danger: "bg-red-600 text-white",
    info: "bg-blue-600 text-white",
  };
  return (
    <Link to={to} className="group">
      <Card className="p-5 h-full hover:shadow-md transition-shadow duration-150 border">
        <div className="flex items-start justify-between">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="mt-4">
          <div className="text-2xl font-extrabold font-heading tracking-tight tabular-nums">{value}</div>
          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-0.5">{label}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Učitavanje...</div>;
  const s = data.stats;
  const chart = data.monthly_expenses.map((m) => ({ name: monthLabel(m.month), total: m.total }));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold font-heading tracking-tight">Pregled poslovanja</h1>
        <p className="text-sm text-muted-foreground mt-1">Trenutno stanje vašeg poslovanja na jednom mjestu</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi icon={Briefcase} tone="primary" label="Neto zbir za realizaciju" value={<span className="flex flex-col leading-tight"><span className="text-xl">{formatMoney(s.deals_value_km, "KM")}</span><span className="text-xl text-emerald-600 dark:text-emerald-400">{formatMoney(s.deals_value_eur, "EUR")}</span></span>} sub={`${s.deals_active_count} poslova za realizaciju`} to="/kupci" />
        <Kpi icon={Wallet} tone="info" label="Troškovi ovog mjeseca (neto)" value={formatKM(s.month_expenses)} sub="Tekući mjesec · neto trošak" to="/troskovi" />
        <Kpi icon={CalendarClock} tone="warning" label="Hitni rokovi" value={s.deadlines_urgent_count} sub={`${s.deadlines_active_count} ukupno aktivnih`} to="/rokovi" />
        <Kpi icon={FileWarning} tone="danger" label="Neplaćene fakture" value={formatKM(s.invoices_unpaid_total)} sub={`${s.invoices_unpaid_count} faktura · ${s.invoices_overdue_count} kasni`} to="/fakture" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="h-5 w-5 text-slate-500" />
            <h3 className="font-heading font-bold text-lg">Troškovi (zadnjih 6 mjeseci)</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={chart} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={12} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => formatKM(v)} contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                  {chart.map((e, i) => <Cell key={i} fill="hsl(var(--chart-1))" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Nadolazeći rokovi</h3>
            <Link to="/rokovi" className="text-xs text-blue-600 font-semibold hover:underline">Svi →</Link>
          </div>
          <div className="space-y-3">
            {data.urgent_deadlines.length === 0 && <p className="text-sm text-muted-foreground">Nema aktivnih rokova.</p>}
            {data.urgent_deadlines.map((d) => {
              const lbl = deadlineLabel(d.due_date);
              return (
                <div key={d.id} className="flex items-center justify-between gap-2 border-b pb-3 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <div className="text-xs text-muted-foreground">{d.category} · {formatDate(d.due_date)}</div>
                  </div>
                  <Badge tone={lbl.tone}>{lbl.text}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Nedavni poslovi</h3>
            <Link to="/kupci" className="text-xs text-blue-600 font-semibold hover:underline">Svi →</Link>
          </div>
          <div className="space-y-3">
            {data.recent_deals.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 border-b pb-3 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.company_name || d.customer_name || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.title}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">{formatMoney(d.amount != null && d.amount !== "" ? d.amount : d.value, d.country === "Bosna i Hercegovina" ? "KM" : "EUR")}</div>
                  <div className="text-xs text-muted-foreground">{d.stage}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Fakture za platiti</h3>
            <Link to="/fakture" className="text-xs text-blue-600 font-semibold hover:underline">Sve →</Link>
          </div>
          <div className="space-y-3">
            {data.unpaid_invoices.length === 0 && <p className="text-sm text-muted-foreground">Nema neplaćenih faktura.</p>}
            {data.unpaid_invoices.map((i) => {
              const lbl = deadlineLabel(i.due_date);
              return (
                <div key={i.id} className="flex items-center justify-between gap-2 border-b pb-3 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{i.supplier_name}</div>
                    <div className="text-xs text-muted-foreground">#{i.invoice_number} · {formatDate(i.due_date)}</div>
                  </div>
                  <div className="text-right shrink-0 space-y-1">
                    <div className="text-sm font-semibold tabular-nums">{formatKM(i.amount)}</div>
                    <Badge tone={lbl.tone}>{lbl.text}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
